import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Fuse from 'fuse.js';

import MagnifyingGlass from '../icons/MagnifyingGlass';
import debounce from '../utils/debounce';
import { Input } from '../Input/Input';

import ModalHeader from '../ModalHeader';

import AutocompleterNoResult from './AutocompleterNoResult';
import AutocompleterItem from './AutocompleterItem';

import handlers from './navigationHandlers';
import { ENTER_KEY } from './constants';

import normalizeListValue from './utils';
import styles from './Autocompleter.module.scss';

const FILTER_THRESHOLD = 0.7;
const DISPLAY_THRESHOLD = 0.4;

const FUSE_OPTIONS = {
  threshold: FILTER_THRESHOLD,
  includeScore: true
};

const normalizeList = list =>
  list.reduce(
    (acc, cityName) => ({
      ...acc,
      [normalizeListValue(cityName)]: cityName
    }),
    {}
  );

const generatePartialStateBasedOnFilter = (
  keyword,
  currentIndex,
  normalizedCityNames
) => {
  const mustRender = true;
  const fuse = new Fuse(Object.keys(normalizedCityNames), FUSE_OPTIONS);
  const fuseSuggestions = fuse.search(keyword);
  const nResults = fuseSuggestions.length;
  let selectedIndex = -1;

  if (nResults) {
    selectedIndex = currentIndex >= nResults ? nResults - 1 : currentIndex;
  }

  const suggestions = fuseSuggestions.filter(
    item => item.score < DISPLAY_THRESHOLD
  );
  const recommendedItemsBasedOnSearch = suggestions.map(obj => obj.item);

  return {
    filtered: recommendedItemsBasedOnSearch,
    mustRender,
    selectedIndex
  };
};

const mustFilter = (keyword, minLength) => keyword.length >= minLength;

const noOp = () => {};

const Autocompleter = ({
  list = [],
  minLength = 1,
  placeholder = '',
  typingPlaceholder = '',
  iconColor = undefined,
  onClick = noOp,
  onChange = noOp,
  onSelectedElement = noOp,
  onNoResults = noOp,
  onCloseModal = noOp,
  onSubmitSuggestion = noOp,
  initialValue = '',
  hasError = false,
  className = '',
  dataTest = '',
  name = 'autocompleter',
  onBlur = noOp,
  featuredResults = [],
  floatingResults = true,
  otherCitySuggestionsEnabled = false,
  showMobileFullscreen = false,
  selectAllContentOnFocus = true,
  showIcon = true
}) => {
  const [value, setValue] = useState(initialValue);
  const [height, setHeight] = useState('0px');
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [filtered, setFiltered] = useState([]);
  const [mustRender, setMustRender] = useState(false);
  const [showTypingHint, setShowTypingHint] = useState(false);
  const [showFeaturedResults, setShowFeaturedResults] = useState(false);

  const inputRef = useRef(null);
  const listContainerRef = useRef(null);
  const parentNodeRef = useRef(null);
  const normalizedList = useMemo(
    () => normalizeList(list),
    [JSON.stringify(list)]
  );

  const callOnNoResults = useCallback(
    debounce(() => {
      onNoResults(value);
    }, 250),
    [onNoResults, value]
  );

  useEffect(() => {
    const closeAutocompleter = e => {
      if (!parentNodeRef.current.contains(e.target)) {
        setMustRender(false);
        setShowFeaturedResults(false);
        setShowTypingHint(false);
        setHeight('0px');
      }
    };

    document.addEventListener('click', closeAutocompleter);
    return () => {
      document.removeEventListener('click', closeAutocompleter);
    };
  }, []);

  useEffect(() => {
    const finalHeight = listContainerRef.current?.children.length
      ? `${listContainerRef.current.offsetHeight}px`
      : '0px';

    requestAnimationFrame(() => {
      setHeight(finalHeight);
    });
  }, [filtered, showFeaturedResults, featuredResults]);

  useEffect(() => {
    if (initialValue !== value) {
      setValue(initialValue);
    }
  }, [initialValue, JSON.stringify(list)]);

  const handleChange = e => {
    const { value: inputValue } = e.target;

    if (mustFilter(inputValue, minLength)) {
      const partialState = generatePartialStateBasedOnFilter(
        normalizeListValue(inputValue),
        selectedIndex,
        normalizedList
      );
      setValue(inputValue);
      setShowFeaturedResults(false);
      setShowTypingHint(false);
      setFiltered(partialState.filtered);
      setMustRender(partialState.mustRender);
      setSelectedIndex(partialState.selectedIndex);
    } else {
      setValue(inputValue);
      setFiltered([]);
      setMustRender(false);
    }
    onChange(inputValue);
  };

  const handleOnBlur = event => {
    const { value: blurValue } = event.target;
    onBlur({ name, value: blurValue });
  };

  const handleEndSelection = selectedValue => {
    const normalizedSelectedValue = normalizeListValue(selectedValue);
    const selectedValueDenormalized = normalizedList[normalizedSelectedValue];
    setValue(selectedValue);
    setHeight('0px');
    setSelectedIndex(-1);
    setFiltered([]);
    setMustRender(false);
    setShowTypingHint(false);
    setShowFeaturedResults(false);
    onSelectedElement(selectedValueDenormalized);
  };

  const handleNavigation = e => {
    if (typeof handlers[e.keyCode] === 'function') {
      e.preventDefault();
      const newState = handlers[e.keyCode]({
        value,
        selectedIndex,
        filtered
      });
      if (newState) {
        if (newState.selectedIndex !== undefined) {
          setSelectedIndex(newState.selectedIndex);
        }
        if (newState.value !== undefined) {
          setValue(newState.value);
        }
      }
    } else if (e.keyCode === ENTER_KEY) {
      e.preventDefault();
      if (selectedIndex >= 0) {
        handleEndSelection(filtered[selectedIndex]);
      }
    }
  };

  const handleFocus = e => {
    e.preventDefault();
    if (selectAllContentOnFocus) {
      inputRef.current.select();
    }
    if (!value) {
      setShowFeaturedResults(true);
      setShowTypingHint(true);
    }
    onClick();
  };

  const handleSubmitSuggestion = () => {
    onSubmitSuggestion();
    onCloseModal();
    setMustRender(false);
    setShowTypingHint(false);
  };

  const renderFilteredResults = keyword => {
    const MAX_RESULTS_TO_SHOW = 5;
    return filtered
      .slice(0, MAX_RESULTS_TO_SHOW)
      .map(item => (
        <AutocompleterItem
          item={normalizedList[item]}
          key={`${item}`}
          keyword={keyword}
          onClick={handleEndSelection}
          selected={selectedIndex === filtered.indexOf(item)}
        />
      ));
  };

  const renderNoResults = () => {
    callOnNoResults();
    return (
      <AutocompleterNoResult
        value={value}
        otherCitySuggestionsEnabled={otherCitySuggestionsEnabled}
        onSubmitSuggestion={handleSubmitSuggestion}
      />
    );
  };

  const renderList = keyword =>
    filtered.length ? renderFilteredResults(keyword) : renderNoResults();

  const renderOptions = () => {
    const normalisedValue = value.toLocaleLowerCase();
    return mustRender ? renderList(normalisedValue) : [];
  };

  return (
    <div
      className={classNames(
        styles.autocompleter,
        {
          [styles['autocompleter--active']]: mustRender,
          [styles['autocompleter--error']]: hasError,
          [styles['autocompleter--fullscreen']]: showMobileFullscreen
        },
        className
      )}
      ref={parentNodeRef}
    >
      {showMobileFullscreen && (
        <ModalHeader
          showBack
          showClose={false}
          className="autocompleter__header"
          onRequestClose={onCloseModal}
          dataTest="autocompleter-fullscreen-header"
        />
      )}

      <div className={styles['autocompleter__input-container']}>
        {showIcon && (
          <MagnifyingGlass
            color={iconColor}
            className={styles.autocompleter__glass}
          />
        )}
        <Input
          className={styles.autocompleter__input}
          onChange={handleChange}
          onBlur={handleOnBlur}
          onFocus={handleFocus}
          onKeyDown={handleNavigation}
          placeholder={
            typingPlaceholder && showTypingHint
              ? typingPlaceholder
              : placeholder
          }
          ref={inputRef}
          type="text"
          value={value}
          dataTest={dataTest}
          name={name}
        />
      </div>

      <div
        style={{ height }}
        className={classNames(styles['autocompleter__list-wrapper'], {
          [styles['autocompleter__list-wrapper--floating']]: floatingResults
        })}
        data-test="autocompleter-results"
        data-list-loaded={list.length > 0}
      >
        <ul
          className={styles['autocompleter__item-container']}
          ref={listContainerRef}
          data-test={dataTest}
        >
          {!!featuredResults.length && showFeaturedResults
            ? featuredResults.map(item => (
                <AutocompleterItem
                  item={item}
                  key={`${item}_${value}`}
                  onClick={handleEndSelection}
                />
              ))
            : renderOptions()}
        </ul>
      </div>
    </div>
  );
};

Autocompleter.propTypes = {
  list: PropTypes.arrayOf(PropTypes.string),
  minLength: PropTypes.number,
  placeholder: PropTypes.string,
  typingPlaceholder: PropTypes.string,
  iconColor: PropTypes.string,
  onClick: PropTypes.func,
  onChange: PropTypes.func,
  onSelectedElement: PropTypes.func,
  onNoResults: PropTypes.func,
  onCloseModal: PropTypes.func,
  onSubmitSuggestion: PropTypes.func,
  initialValue: PropTypes.string,
  hasError: PropTypes.bool,
  className: PropTypes.string,
  dataTest: PropTypes.string,
  name: PropTypes.string,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  featuredResults: PropTypes.arrayOf(PropTypes.string),
  floatingResults: PropTypes.bool,
  otherCitySuggestionsEnabled: PropTypes.bool,
  showMobileFullscreen: PropTypes.bool,
  selectAllContentOnFocus: PropTypes.bool,
  showIcon: PropTypes.bool
};

export default Autocompleter;
